import { useState, useEffect } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { updateStylistDetails, getStylistDetails } from '@services/stylistService';
import { getPlans } from '@services/reservationService';
import { getCertifications } from '@services/certificationService';
import { getSubServiceTypes } from '@services/subServiceTypeService';
import { getAreaLocations } from '@services/areaService';
import { getAreas } from '@services/profileService';
import { getShowcases, deleteShowcase } from '@services/showcaseService';
import { toast } from 'react-toastify';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Button,
  Select as MuiSelect,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Tooltip,
  CircularProgress,
  FormHelperText,
  Grid,
  Divider
} from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from '@mui/icons-material/Delete';
import { translateTextStream } from '@services/aiTranslateService';
import AddressAutocomplete from '../common/AddressAutocomplete';
import GoogleMapsLinks from '../common/GoogleMapsLinks';
import AreaSuggestion from '../common/AreaSuggestion';
import ShowcaseCard from '../showcase/ShowcaseCard';
import ShowcaseForm from '../showcase/ShowcaseForm';
import type { ShowcaseFormProps } from '../showcase/ShowcaseForm';
import { matchPlaceToAreaWithAutoSelect } from '../../utils/areaMatchingUtils';
import type { RegionData as AreaUtilRegionData } from '../../utils/areaMatchingUtils';

interface AreaLocation {
  _id: string;
  name: string;
  zh_name: string;
  english?: string;
  chinese?: string;
  [key: string]: unknown;
}

interface AreaRegion {
  _id: string;
  region_chinese: string;
  region_english: string;
  locations?: Array<{ _id: string; chinese: string; english: string; location_code?: string }>;
  [key: string]: unknown;
}

interface SubServiceTypeOption {
  _id: string;
  name: string;
  zh_name: string;
  [key: string]: unknown;
}

interface CertificationOption {
  _id: string;
  name: string;
  zh_name: string;
  org_name?: string;
  zh_org_name?: string;
  scope?: string;
  zh_scope?: string;
  recognization?: string;
  zh_recognization?: string;
  [key: string]: unknown;
}

interface PlanOption {
  _id: string;
  name: string;
  [key: string]: unknown;
}

interface ShowcaseMedia {
  key: string;
  mediaType: string;
}

interface ShowcaseData {
  _id: string;
  media?: ShowcaseMedia[];
  content?: string;
  status?: string;
  planIds?: string[];
  isFromInstagram?: boolean;
  serviceTypeIds?: string[];
}

interface AreaMatch {
  location_id?: string;
  region_id: string;
  chinese: string;
  english: string;
  region_chinese: string;
  region_english: string;
  [key: string]: unknown;
}

interface AreaSuggestionItem extends AreaMatch {
  _id: string;
  score: number;
}

interface AreaSuggestionResult {
  match: AreaMatch | null;
  confidence: number;
  suggestions: AreaSuggestionItem[];
  debugInfo?: Record<string, unknown>;
  shouldAutoSelect?: boolean;
  shouldShowSuggestion?: boolean;
  autoSelectReason?: string;
  [key: string]: unknown;
}

interface AcceptSuggestionPayload {
  regionId: string;
  locationId: string;
  match: AreaMatch | AreaSuggestionItem;
}

interface PlaceSelectedResult {
  address: string;
  coordinates: [number, number] | null;
  place_id: string;
}

type LanguageCode = 'en' | 'zh' | 'zh-HK' | 'zh-TW';

type FormDataValue = string | number | boolean | File | Blob | null | undefined | FormDataValue[] | { [key: string]: FormDataValue };

interface DepositOptions {
  isRequired: boolean;
  paymeQRCodeKey: string | null;
  rules: string;
  zh_rules: string;
}

interface ProfileFormData {
  _id: string;
  displayName: string;
  useBusinessAddress: boolean;
  businessAddress: string;
  businessArea: AreaLocation | null;
  coordinates: number[];
  placeId: string;
  yearOfExperience: number;
  featuredSkills: Array<{ name: string; zh_name: string }>;
  certifications: CertificationOption[];
  subServiceTypes: SubServiceTypeOption[];
  availability: {
    dayoff: number[];
    startHour: string;
    endHour: string;
    leaveDates: string[];
  };
  description: string;
  zh_description: string;
  assignedPlans: string[];
  avatarKey: string | null;
  canCommunicateInEnglish: boolean;
  isAcceptPlatformCoupons: boolean;
  depositOptions: DepositOptions;
  paymeQRCodeFile: File | null;
  avatarFile: File | null;
}

interface StylistData {
  _id?: string;
  displayName?: string;
  useBusinessAddress?: boolean;
  businessAddress?: string;
  businessArea?: AreaLocation | string | null;
  coordinates?: number[];
  placeId?: string;
  yearOfExperience?: number;
  featuredSkills?: Array<{ name: string; zh_name: string }>;
  certifications?: CertificationOption[] | string[];
  subServiceTypes?: SubServiceTypeOption[] | string[];
  availability?: {
    dayoff?: number[];
    startHour?: string;
    endHour?: string;
    leaveDates?: string[];
  };
  startHour?: string;
  endHour?: string;
  leaveDates?: string[];
  description?: string;
  zh_description?: string;
  assignedPlans?: string[];
  avatarKey?: string | null;
  canCommunicateInEnglish?: boolean;
  isAcceptPlatformCoupons?: boolean;
  depositOptions?: {
    isRequired?: boolean;
    paymeQRCodeKey?: string | null;
    rules?: string;
    zh_rules?: string;
  };
  [key: string]: unknown;
}

interface StylistProfileSettingsProps {
  stylist?: StylistData;
  fetchStylists?: () => void;
  onClose?: () => void;
}

interface DayOffOption {
  value: number;
  label: string;
}

const dayOffOptions: DayOffOption[] = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 7, label: 'Sunday' }
];

const StylistProfileSettings = ({ stylist, fetchStylists, onClose }: StylistProfileSettingsProps): React.JSX.Element => {
  const { t } = useTranslation();
  const { register, handleSubmit, setValue, control, reset, formState: { errors }, watch, getValues } = useForm<ProfileFormData>({
    defaultValues: {
      _id: stylist?._id || '',
      displayName: stylist?.displayName || '',
      useBusinessAddress: stylist?.useBusinessAddress ?? true,
      businessAddress: stylist?.businessAddress || '',
      businessArea: (typeof stylist?.businessArea === 'object' ? stylist?.businessArea : null) as AreaLocation | null,
      coordinates: stylist?.coordinates || [],
      placeId: stylist?.placeId || '',
      yearOfExperience: stylist?.yearOfExperience || 0,
      featuredSkills: (stylist?.featuredSkills as Array<{ name: string; zh_name: string }>) || [{ name: '', zh_name: '' }],
      certifications: (stylist?.certifications as CertificationOption[]) || [],
      subServiceTypes: (stylist?.subServiceTypes as SubServiceTypeOption[]) || [],
      availability: {
        dayoff: stylist?.availability?.dayoff || [],
        startHour: stylist?.startHour || '',
        endHour: stylist?.endHour || '',
        leaveDates: stylist?.leaveDates || [],
      },
      description: stylist?.description || '',
      zh_description: stylist?.zh_description || '',
      assignedPlans: stylist?.assignedPlans || [],
      avatarKey: stylist?.avatarKey || null,
      canCommunicateInEnglish: stylist?.canCommunicateInEnglish || false,
      isAcceptPlatformCoupons: stylist?.isAcceptPlatformCoupons || false,
      depositOptions: {
        isRequired: stylist?.depositOptions?.isRequired || false,
        paymeQRCodeKey: stylist?.depositOptions?.paymeQRCodeKey || null,
        rules: stylist?.depositOptions?.rules || '',
        zh_rules: stylist?.depositOptions?.zh_rules || ''
      },
      paymeQRCodeFile: null,
      avatarFile: null
    }
  });

  const { fields: featuredSkills, append: addSkill, remove: removeSkill } = useFieldArray({
    control,
    name: 'featuredSkills'
  });

  const [leaveDates, setLeaveDates] = useState<Dayjs[]>(
    stylist?.availability?.leaveDates?.map((date: string) => dayjs(date)) || []
  );

  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [openCalendar, setOpenCalendar] = useState<boolean>(false);
  const avatarKey = watch('avatarKey');
  const avatarFile = watch('avatarFile');
  const [certifications, setCertifications] = useState<CertificationOption[]>([]);
  const [areaLocations, setAreaLocations] = useState<AreaLocation[]>([]);
  const [subServiceTypeOptions, setSubServiceTypeOptions] = useState<SubServiceTypeOption[]>([]);
  const [isTranslatingEnToZhDesc, setIsTranslatingEnToZhDesc] = useState<boolean>(false);
  const [isTranslatingZhToEnDesc, setIsTranslatingZhToEnDesc] = useState<boolean>(false);
  const [isTranslatingEnToZhDepositRules, setIsTranslatingEnToZhDepositRules] = useState<boolean>(false);
  const [isTranslatingZhToEnDepositRules, setIsTranslatingZhToEnDepositRules] = useState<boolean>(false);

  const descriptionValue = watch('description');
  const zhDescriptionValue = watch('zh_description');
  const watchedFeaturedSkills = watch('featuredSkills');
  const useBusinessAddress = watch('useBusinessAddress');
  const placeId = watch('placeId');
  const businessAddress = watch('businessAddress');
  const depositIsRequired = watch('depositOptions.isRequired');
  const depositRules = watch('depositOptions.rules') || '';
  const zhDepositRules = watch('depositOptions.zh_rules') || '';
  const paymeQRCodeFile = watch('paymeQRCodeFile');
  const paymeQRCodeKey = watch('depositOptions.paymeQRCodeKey');

  // Add loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Area matching state
  const [areaData, setAreaData] = useState<AreaRegion[]>([]);
  const [areaSuggestion, setAreaSuggestion] = useState<AreaSuggestionResult | null>(null);

  // Showcase management state
  const [showcases, setShowcases] = useState<ShowcaseData[]>([]);
  const [isLoadingShowcases, setIsLoadingShowcases] = useState<boolean>(false);
  const [selectedShowcase, setSelectedShowcase] = useState<ShowcaseData | null>(null);
  const [showShowcaseForm, setShowShowcaseForm] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [showcaseToDelete, setShowcaseToDelete] = useState<ShowcaseData | null>(null);

  useEffect(() => {
    const fetchDetails = async (): Promise<void> => {
      let details: StylistData | undefined = stylist;
      if (!stylist) {
        const fetchedStylist = await getStylistDetails('');
        details = fetchedStylist as unknown as StylistData;
        setLeaveDates(details.availability?.leaveDates?.map((date: string) => dayjs(date)) || []);
      }

      const plansResponse = await getPlans();
      const fetchedPlans = plansResponse?.data || [];
      setPlans(fetchedPlans as PlanOption[]);

      let fetchedCerts: CertificationOption[] = [];
      try {
        const response = await getCertifications();
        fetchedCerts = (response.certifications || []) as CertificationOption[];
        setCertifications(fetchedCerts);
      } catch (error: unknown) {
        console.error('Error fetching certifications:', error);
        toast.error(t('stylistProfileSettings.errorFetchingCertifications'));
      }

      let fetchedAreaLocations: AreaLocation[] = [];
      try {
        const areaLocationsResponse = await getAreaLocations();
        fetchedAreaLocations = (areaLocationsResponse.locations || []) as AreaLocation[];
        setAreaLocations(fetchedAreaLocations);
      } catch (error: unknown) {
        console.error('Error fetching area locations:', error);
        toast.error(t('stylistProfileSettings.errorFetchingAreaLocations'));
      }

      let fetchedAreaData: AreaRegion[] = [];
      try {
        const areasData = await getAreas();
        fetchedAreaData = areasData as AreaRegion[];
        setAreaData(fetchedAreaData);
      } catch (error: unknown) {
        console.error('Error fetching area data:', error);
      }

      let fetchedSubServiceTypes: SubServiceTypeOption[] = [];
      try {
        const subServiceTypesResponse = await getSubServiceTypes();
        fetchedSubServiceTypes = (subServiceTypesResponse.subServiceTypes || []) as SubServiceTypeOption[];
        setSubServiceTypeOptions(fetchedSubServiceTypes);
      } catch (error: unknown) {
        console.error('Error fetching sub-service types:', error);
        toast.error(t('stylistProfileSettings.errorFetchingSubServiceTypes'));
      }

      const hydratedSubServiceTypes = (details?.subServiceTypes || [])
        .map((subService: SubServiceTypeOption | string) => {
          const idToFind = typeof subService === 'string' ? subService : subService._id;
          return fetchedSubServiceTypes.find(option => option._id === idToFind);
        })
        .filter(Boolean) as SubServiceTypeOption[];

      const hydratedCertifications = (details?.certifications || [])
        .map((cert: CertificationOption | string) => {
          const idToFind = typeof cert === 'string' ? cert : cert._id;
          return fetchedCerts.find(option => option._id === idToFind);
        })
        .filter(Boolean) as CertificationOption[];

      // Hydrate businessArea from ObjectId to full location object
      let hydratedBusinessArea: AreaLocation | null = null;
      const businessAreaId = details?.businessArea;
      if (businessAreaId) {
        const idToFind = typeof businessAreaId === 'string' ? businessAreaId : (businessAreaId as AreaLocation)._id;
        hydratedBusinessArea = fetchedAreaLocations.find(loc => loc._id === idToFind) || null;
      }

      const processedDetails = {
        ...details,
        subServiceTypes: hydratedSubServiceTypes,
        certifications: hydratedCertifications,
        businessArea: hydratedBusinessArea
      };

      if (processedDetails?.assignedPlans) {
        // Ensure fetchedPlans is an array before using .find()
        const plansArray = fetchedPlans as PlanOption[];
        const mappedPlans = (processedDetails.assignedPlans as string[]).map((planId: string) => {
          const plan = plansArray.find(plan => plan._id === planId);
          return plan ? plan._id : '';
        });
        reset({
          ...processedDetails,
          assignedPlans: mappedPlans
        } as ProfileFormData);
      } else {
        reset(processedDetails as ProfileFormData);
      }
    };

    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stylist, reset]);

  // Fetch showcases for the stylist
  useEffect(() => {
    const fetchShowcases = async (): Promise<void> => {
      if (!stylist?._id) return;

      setIsLoadingShowcases(true);
      try {
        const showcaseResponse = await getShowcases(stylist._id);
        setShowcases((showcaseResponse?.data || []) as ShowcaseData[]);
      } catch (error: unknown) {
        console.error('Error fetching showcases:', error);
        toast.error(t('stylistProfileSettings.errorFetchingShowcases'));
      } finally {
        setIsLoadingShowcases(false);
      }
    };

    fetchShowcases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stylist]);

  useEffect(() => {
    if (stylist?.availability?.dayoff) {
      setValue('availability.dayoff', stylist.availability.dayoff);
    }
  }, [stylist, setValue]);

  // Add useEffect to initialize fields when deposit is required
  useEffect(() => {
    if (depositIsRequired) {
      // Make sure deposit rules fields are initialized when checkbox is first checked
      if (!getValues('depositOptions.rules')) {
        setValue('depositOptions.rules', '');
      }
      if (!getValues('depositOptions.zh_rules')) {
        setValue('depositOptions.zh_rules', '');
      }
    }
  }, [depositIsRequired, getValues, setValue]);

  // Store businessAddress value to preserve it during checkbox toggles
  const [preservedAddress, setPreservedAddress] = useState<string>('');
  const [preservedCoordinates, setPreservedCoordinates] = useState<number[]>([]);
  const [preservedPlaceId, setPreservedPlaceId] = useState<string>('');

  // Add useEffect to preserve businessAddress when toggling useBusinessAddress checkbox
  useEffect(() => {
    const currentAddress = getValues('businessAddress');
    const currentCoordinates = getValues('coordinates');
    const currentPlaceId = getValues('placeId');

    // When useBusinessAddress changes to true, preserve the current values
    if (useBusinessAddress === true && currentAddress) {
      setPreservedAddress(currentAddress);
      setPreservedCoordinates(currentCoordinates || []);
      setPreservedPlaceId(currentPlaceId || '');
    }
    // When useBusinessAddress changes to false, restore preserved values if they exist
    else if (useBusinessAddress === false && preservedAddress && !currentAddress) {
      setValue('businessAddress', preservedAddress, { shouldValidate: true });
      if (preservedCoordinates.length > 0) {
        setValue('coordinates', preservedCoordinates, { shouldValidate: true });
      }
      if (preservedPlaceId) {
        setValue('placeId', preservedPlaceId, { shouldValidate: true });
      }
    }
  }, [useBusinessAddress, getValues, setValue, preservedAddress, preservedCoordinates, preservedPlaceId]);

  const handleAvatarSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files && event.target.files.length > 0) {
      setValue('avatarFile', event.target.files[0]);
    }
  };

  const handlePaymeQRCodeSelect = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.files && event.target.files.length > 0) {
      setValue('paymeQRCodeFile', event.target.files[0]);
    }
  };

  const onSubmit = async (data: ProfileFormData): Promise<void> => {
    // Check if startHour and endHour are provided and valid
    const isInvalidTime = (time: string): boolean => !time || time.includes('NaN');

    console.log('[StylistProfileSettings] onSubmit triggered. Final data:', data);
    console.log('[StylistProfileSettings] businessAddress value:', data.businessAddress);
    console.log('[StylistProfileSettings] coordinates value:', data.coordinates);

    if (isInvalidTime(data.availability.startHour) || isInvalidTime(data.availability.endHour)) {
      toast.error(t('stylistProfileSettings.hoursRequired'));
      return;
    }

    // Set submitting state to true
    setIsSubmitting(true);

    try {
      // Remove empty elements from assignedPlans
      const cleanedAssignedPlans = (data.assignedPlans || []).filter(Boolean);

      // Make sure deposit options are correctly included
      console.log("Deposit options before submission:", data.depositOptions);

      const updatedData: Record<string, FormDataValue> = {
        ...data,
        assignedPlans: cleanedAssignedPlans,
        businessArea: data.businessArea?._id,
        certifications: (data.certifications || []).map(c => c._id),
        subServiceTypes: (data.subServiceTypes || []).map(s => s._id),
        availability: {
          ...data.availability,
          leaveDates: leaveDates.map(date => {
            const jsDate = date instanceof Date ? date : date.toDate();
            const year = jsDate.getFullYear();
            const month = String(jsDate.getMonth() + 1).padStart(2, '0');
            const day = String(jsDate.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          }),
          dayoff: data.availability.dayoff.map(Number)
        },
        // Explicitly include deposit options in the updated data
        depositOptions: {
          isRequired: data.depositOptions.isRequired,
          paymeQRCodeKey: data.depositOptions.paymeQRCodeKey,
          rules: data.depositOptions.rules || '',
          zh_rules: data.depositOptions.zh_rules || ''
        }
      };

      console.log("Full data being sent:", updatedData);
      await updateStylistDetails(updatedData);
      fetchStylists?.();
      onClose?.();
      toast.success(t('stylistProfileSettings.profileUpdated'));
    } catch (error: unknown) {
      console.error("Error updating stylist details:", error);
      const err = error as { message?: string };
      toast.error(err.message || t('common.errorOccurred'));
    } finally {
      // Set submitting state back to false
      setIsSubmitting(false);
    }
  };

  const handleStreamTranslate = async (
    sourceText: string | undefined,
    sourceLang: LanguageCode,
    targetLang: LanguageCode,
    targetFieldName: string,
    setLoadingState: (loading: boolean) => void
  ): Promise<void> => {
    if (!sourceText) {
      toast.info('Source field for translation is empty.');
      return;
    }
    setLoadingState(true);

    try {
      const formCompatibleSetTargetState = (valueOrUpdater: string | ((prev: string) => string)): void => {
        const currentFieldValue = (getValues(targetFieldName as keyof ProfileFormData) as string) || '';
        if (typeof valueOrUpdater === 'function') {
          setValue(targetFieldName as keyof ProfileFormData, valueOrUpdater(currentFieldValue) as ProfileFormData[keyof ProfileFormData], { shouldDirty: true, shouldTouch: true });
        } else {
          setValue(targetFieldName as keyof ProfileFormData, valueOrUpdater as ProfileFormData[keyof ProfileFormData], { shouldDirty: true, shouldTouch: true });
        }
      };

      await translateTextStream(
        sourceText,
        targetLang,
        sourceLang,
        formCompatibleSetTargetState,
        () => {
          setLoadingState(false);
          toast.success('Translation streamed successfully!');
        },
        (error: Error) => {
          setLoadingState(false);
          toast.error('Translation failed: ' + (error.message || 'Unknown error'));
        }
      );
    } catch (error: unknown) {
      console.error('Translation error:', error);
      setLoadingState(false);
      const err = error as { message?: string };
      toast.error('Translation failed: ' + (err.message || 'Unknown error'));
    }
  };

  const handleAreaSuggestionDismiss = (): void => {
    setAreaSuggestion(null);
  };

  const handleAreaSuggestionAccept = (payload: AcceptSuggestionPayload): void => {
    console.log('[StylistProfileSettings] Accepting area suggestion:', payload);
    // Set the business area based on the suggestion - find full object from areaLocations
    const fullArea = areaLocations.find(loc => loc._id === payload.locationId);
    setValue('businessArea', fullArea || null, { shouldValidate: true });

    // Clear the suggestion
    setAreaSuggestion(null);

    // Show success message
    toast.success(t('areaSuggestion.accepted', {
      area: payload.match.chinese || payload.match.english
    }));
  };

  // Showcase management handlers
  const handleEditShowcase = (showcase: ShowcaseData): void => {
    setSelectedShowcase(showcase);
    setShowShowcaseForm(true);
  };

  const handleDeleteShowcase = (showcase: ShowcaseData): void => {
    setShowcaseToDelete(showcase);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDeleteShowcase = async (): Promise<void> => {
    if (!showcaseToDelete) return;

    try {
      await deleteShowcase(showcaseToDelete._id);
      toast.success(t('stylistProfileSettings.showcaseDeletedSuccess'));
      setIsDeleteDialogOpen(false);
      setShowcaseToDelete(null);

      // Refresh showcases
      if (stylist?._id) {
        const showcaseResponse = await getShowcases(stylist._id);
        setShowcases((showcaseResponse?.data || []) as ShowcaseData[]);
      }
    } catch (error: unknown) {
      console.error('Error deleting showcase:', error);
      toast.error(t('stylistProfileSettings.showcaseDeleteError'));
    }
  };

  const handleCloseShowcaseForm = async (): Promise<void> => {
    setShowShowcaseForm(false);
    setSelectedShowcase(null);

    // Refresh showcases
    if (stylist?._id) {
      try {
        const showcaseResponse = await getShowcases(stylist._id);
        setShowcases((showcaseResponse?.data || []) as ShowcaseData[]);
      } catch (error: unknown) {
        console.error('Error fetching showcases:', error);
      }
    }
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}
    >
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 3 }}>
        {(isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
          isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {/* All form controls are wrapped in this scrollable Box */}

        {/* Avatar Upload */}
        <FormControl fullWidth margin="normal">
          <Typography variant="subtitle1" gutterBottom>
            {t('stylistProfileSettings.uploadAvatar')}*
          </Typography>
          <Button
            variant="contained"
            component="label"
            fullWidth
            disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
              isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
          >
            {t('stylistProfileSettings.selectAvatar')}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleAvatarSelect}
            />
          </Button>
          {avatarKey && !avatarFile && (
            <Box sx={{ mt: 2 }}>
              <img src={`${import.meta.env.VITE_R2_SALON_ENDPOINT}/${avatarKey}`} alt="Avatar Preview" style={{ width: '150px', height: '150px', borderRadius: '50%' }} />
            </Box>
          )}
          {avatarFile && (
            <Box sx={{ mt: 2 }}>
              <img src={URL.createObjectURL(avatarFile)} alt="Avatar Preview" style={{ width: '150px', height: '150px', borderRadius: '50%' }} />
            </Box>
          )}
        </FormControl>

        {/* Is Able to Communicate in English */}
        <FormControl fullWidth margin="normal">
          <Controller
            name="canCommunicateInEnglish"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={t('stylistProfileSettings.canCommunicateInEnglish')}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                  isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
              />
            )}
          />
        </FormControl>

        {/* Display Name with error */}
        <FormControl fullWidth margin="normal">
          <TextField
            id="displayName"
            label={`${t('stylistProfileSettings.displayName')}*`}
            {...register('displayName', { required: t('common.fieldRequired') })}
            placeholder={t('stylistProfileSettings.enterDisplayName')}
            slotProps={{ inputLabel: { shrink: true } }}
            error={!!errors.displayName}
            helperText={errors.displayName?.message}
            disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
              isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
          />
        </FormControl>

        {/* Use Business Address */}
        <FormControl fullWidth margin="normal">
          <Controller
            name="useBusinessAddress"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={t('stylistProfileSettings.useBusinessAddress')}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                  isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
              />
            )}
          />
        </FormControl>

        {useBusinessAddress === false && (
          <>
            <Box my={2}>
              <Controller
                name="businessAddress"
                control={control}
                render={({ field }) => {
                  console.log(`[StylistProfileSettings] Controller rendering. businessAddress value: "${field.value}"`);
                  return (
                    <AddressAutocomplete
                      key="business-address-autocomplete" // Prevent recreation on checkbox toggle
                      label={t('stylistProfileSettings.businessAddress')}
                      value={field.value}
                      onInputChange={(value: string) => {
                        console.log(`[StylistProfileSettings] onInputChange called with: "${value}"`);
                        field.onChange(value);
                      }}
                      onPlaceSelected={(placeData: PlaceSelectedResult) => {
                        console.log('[StylistProfileSettings] onPlaceSelected called with:', placeData);
                        // placeData structure: { address, coordinates, place_id }
                        // coordinates is an array [lng, lat] for GeoJSON format
                        if (placeData.coordinates && Array.isArray(placeData.coordinates)) {
                          setValue('coordinates', placeData.coordinates, { shouldValidate: true });
                        }
                        // Store the place_id for generating Google Maps links
                        if (placeData.place_id) {
                          setValue('placeId', placeData.place_id, { shouldValidate: true });
                        }
                        // Also update the address field
                        field.onChange(placeData.address);

                        // Perform area matching
                        if (areaData.length > 0) {
                          const matchResult = matchPlaceToAreaWithAutoSelect(
                            { address: placeData.address } as { address: string; [key: string]: unknown },
                            areaData as AreaUtilRegionData[]
                          );
                          console.log('[StylistProfileSettings] Area match result:', matchResult);

                          if (matchResult.shouldAutoSelect && matchResult.match) {
                            // Auto-select the best match - find full object from areaLocations
                            console.log('[StylistProfileSettings] Auto-selecting area:', matchResult.match);
                            const fullArea = areaLocations.find(loc => loc._id === matchResult.match!.location_id);
                            setValue('businessArea', fullArea || null, { shouldValidate: true });
                            toast.success(t('areaSuggestion.autoSelected', {
                              area: matchResult.match.chinese || matchResult.match.english,
                              confidence: matchResult.confidence
                            }));
                          } else if (matchResult.shouldShowSuggestion) {
                            // Show manual suggestion for lower confidence matches
                            setAreaSuggestion(matchResult as AreaSuggestionResult);
                          }
                        }
                      }}
                      disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                        isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
                    />
                  );
                }}
              />
              {/* Hidden field to ensure businessAddress is registered with react-hook-form */}
              <TextField
                {...register('businessAddress')}
                sx={{ display: 'none' }}
                tabIndex={-1}
              />

              {/* Google Maps Links */}
              {placeId && businessAddress && (
                <GoogleMapsLinks placeId={placeId} address={businessAddress} />
              )}

              {/* Area Suggestion */}
              {areaSuggestion && (
                <AreaSuggestion
                  matchResult={areaSuggestion}
                  onAcceptSuggestion={handleAreaSuggestionAccept}
                  onDismiss={handleAreaSuggestionDismiss}
                  currentSelection={watch('businessArea')?._id}
                  showDebugInfo={import.meta.env.DEV}
                />
              )}
            </Box>
            <FormControl fullWidth margin="normal" error={!!errors.businessArea}>
              <InputLabel>{t('stylistProfileSettings.businessArea')}*</InputLabel>
              <Controller
                name="businessArea"
                control={control}
                rules={{ required: useBusinessAddress === false }}
                render={({ field }) => (
                  <MuiSelect
                    value={field.value?._id || ''}
                    onChange={(e) => {
                      const selectedArea = areaLocations.find(loc => loc._id === e.target.value);
                      field.onChange(selectedArea || null);
                    }}
                    label={`${t('stylistProfileSettings.businessArea')}*`}
                    disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                      isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
                  >
                    {areaLocations.map(location => (
                      <MenuItem key={location._id} value={location._id}>
                        {location.zh_name} ({location.name})
                      </MenuItem>
                    ))}
                  </MuiSelect>
                )}
              />
              {errors.businessArea && <FormHelperText>{t('common.fieldRequired')}</FormHelperText>}
            </FormControl>
          </>
        )}

        {/* Year of Experience with error */}
        <FormControl fullWidth margin="normal">
          <TextField
            id="yearOfExperience"
            label={`${t('stylistProfileSettings.yearOfExperience')}*`}
            type="number"
            {...register('yearOfExperience', { required: t('common.fieldRequired'), min: 0 })}
            placeholder={t('stylistProfileSettings.yearOfExperience')}
            error={!!errors.yearOfExperience}
            helperText={errors.yearOfExperience?.message}
            slotProps={{ htmlInput: { min: 0, step: 1 } }}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
              isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
          />
        </FormControl>

        {/* Featured Skills with error */}
        <Typography>{t('stylistProfileSettings.featuredSkills')}</Typography>
        <FormControl fullWidth margin="normal">
          {featuredSkills.map((field, index) => {
            const currentSkill = watchedFeaturedSkills?.[index];
            const currentSkillName = currentSkill?.name;
            const currentSkillZhName = currentSkill?.zh_name;

            return (
              <Box key={field.id} className="skill-item-container" sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: '4px' }}>
                <Box className="skill-row" sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                  {/* Chinese Skill Name with its Translate Button */}
                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      id={`featuredSkills.${index}.zh_name`}
                      label={t('stylistProfileSettings.skillNameChinese')}
                      {...register(`featuredSkills.${index}.zh_name`)}
                      placeholder={t('stylistProfileSettings.skillNameChinese')}
                      error={!!errors.featuredSkills?.[index]?.zh_name}
                      helperText={errors.featuredSkills?.[index]?.zh_name?.message}
                      disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                        isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
                      fullWidth
                      InputLabelProps={{ shrink: !!currentSkillZhName }}
                    />
                    <Button
                      onClick={() => handleStreamTranslate(currentSkillZhName, 'zh-HK', 'en', `featuredSkills.${index}.name`, setIsTranslatingZhToEnDesc)}
                      disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                        isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules || !currentSkillZhName}
                      variant="outlined"
                      size="small"
                      startIcon={isTranslatingZhToEnDesc ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                      sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                    >
                      {t('quickListingWizard.translateWithAI')} {t('common.toEnglish')}
                    </Button>
                  </Box>

                  {/* English Skill Name with its Translate Button */}
                  <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    <TextField
                      id={`featuredSkills.${index}.name`}
                      label={t('stylistProfileSettings.skillName')}
                      {...register(`featuredSkills.${index}.name`)}
                      placeholder={t('stylistProfileSettings.skillName')}
                      error={!!errors.featuredSkills?.[index]?.name}
                      helperText={errors.featuredSkills?.[index]?.name?.message}
                      disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                        isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
                      fullWidth
                      InputLabelProps={{ shrink: !!currentSkillName }}
                    />
                    <Button
                      onClick={() => handleStreamTranslate(currentSkillName, 'en', 'zh-HK', `featuredSkills.${index}.zh_name`, setIsTranslatingEnToZhDesc)}
                      disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                        isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules || !currentSkillName}
                      variant="outlined"
                      size="small"
                      startIcon={isTranslatingEnToZhDesc ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                      sx={{ mt: 0.5, alignSelf: 'flex-start' }}
                    >
                      {t('quickListingWizard.translateWithAI')} {t('common.toChinese')}
                    </Button>
                  </Box>

                  <Button
                    onClick={() => removeSkill(index)}
                    disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                      isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
                    sx={{ mt: errors.featuredSkills?.[index]?.name || errors.featuredSkills?.[index]?.zh_name ? '38px' : '0px' }} // Adjust margin if helper text is present
                  >
                    {t('stylistProfileSettings.remove')}
                  </Button>
                </Box>
              </Box>
            );
          })}
          <Button onClick={() => addSkill({ name: '', zh_name: '' })} disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
            isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}>
            {t('stylistProfileSettings.addSkill')}
          </Button>
        </FormControl>

        {/* Availability */}
        <FormControl fullWidth margin="normal">
          <InputLabel>{t('stylistProfileSettings.dayOff')}</InputLabel>
          <Controller
            name="availability.dayoff"
            control={control}
            render={({ field }) => (
              <MuiSelect
                {...field}
                multiple
                value={field.value.map(Number)}
                onChange={(event) => field.onChange((event.target.value as number[]).map(Number))}
                labelId="dayOff-label"
                label={`${t('stylistProfileSettings.dayOff')}*`}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                  isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
              >
                {dayOffOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </MuiSelect>
            )}
          />
        </FormControl>

        <Box className="working-hours-container" sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 2 }}>
          <Typography>{t('stylistProfileSettings.workingHours')}*</Typography>
          <Controller
            name="availability.startHour"
            control={control}
            render={({ field }) => {
              const formatTime = (time: string): Dayjs | null => {
                if (!time) return null;
                const [hours, minutes] = time.split(':');
                return dayjs().hour(parseInt(hours)).minute(parseInt(minutes));
              };

              return (
                <TimePicker
                  label={t('stylistProfileSettings.startTime')}
                  value={formatTime(field.value)}
                  onChange={(newValue: Dayjs | null) => {
                    if (!newValue || !dayjs.isDayjs(newValue)) {
                      field.onChange('');
                      return;
                    }
                    const hours = String(newValue.hour()).padStart(2, '0');
                    const minutes = String(newValue.minute()).padStart(2, '0');
                    field.onChange(`${hours}:${minutes}`);
                  }}
                  slotProps={{ textField: { size: "small" } }}
                  disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                    isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
                />
              );
            }}
          />
          <Typography>{t('stylistProfileSettings.to')}</Typography>
          <Controller
            name="availability.endHour"
            control={control}
            render={({ field }) => {
              const formatTime = (time: string): Dayjs | null => {
                if (!time) return null;
                const [hours, minutes] = time.split(':');
                return dayjs().hour(parseInt(hours)).minute(parseInt(minutes));
              };

              return (
                <TimePicker
                  label={t('stylistProfileSettings.endTime')}
                  value={formatTime(field.value)}
                  onChange={(newValue: Dayjs | null) => {
                    if (!newValue || !dayjs.isDayjs(newValue)) {
                      field.onChange('');
                      return;
                    }
                    const hours = String(newValue.hour()).padStart(2, '0');
                    const minutes = String(newValue.minute()).padStart(2, '0');
                    field.onChange(`${hours}:${minutes}`);
                  }}
                  slotProps={{ textField: { size: "small" } }}
                  disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                    isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
                />
              );
            }}
          />
        </Box>

        {/* Leave Dates */}
        <FormControl fullWidth margin="normal">
          <InputLabel shrink>{t('stylistProfileSettings.leaveDates')}</InputLabel>
          <Box sx={{ mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => setOpenCalendar(true)}
              disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
            >
              {t('stylistProfileSettings.applyLeave')}
            </Button>

            {/* Calendar Dialog */}
            <Dialog
              open={openCalendar}
              onClose={() => setOpenCalendar(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>{t('stylistProfileSettings.selectLeaveDates')}</DialogTitle>
              <DialogContent>
                <DateCalendar
                  value={null}
                  onChange={(newValue: Dayjs | null) => {
                    if (!newValue) return;
                    const dateStr = dayjs(newValue).format('YYYY-MM-DD');
                    const exists = leaveDates.some(date =>
                      date.format('YYYY-MM-DD') === dateStr
                    );

                    if (exists) {
                      setLeaveDates(leaveDates.filter(date =>
                        date.format('YYYY-MM-DD') !== dateStr
                      ));
                    } else {
                      setLeaveDates([...leaveDates, dayjs(newValue)]);
                    }
                  }}
                />

                {/* Selected Dates Section */}
                <Box sx={{ mt: 2, borderTop: 1, borderColor: 'divider', pt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {t('stylistProfileSettings.selectedDates')}:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {leaveDates.map((date, index) => (
                      <Chip
                        key={index}
                        label={date.format('YYYY-MM-DD')}
                        onDelete={() => {
                          setLeaveDates(leaveDates.filter((_, i) => i !== index));
                        }}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>

                {/* Dialog Actions */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button onClick={() => setOpenCalendar(false)}>
                    {t('common.done')}
                  </Button>
                </Box>
              </DialogContent>
            </Dialog>

            {/* Show total count outside */}
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              {leaveDates.length} {t('stylistProfileSettings.daysSelected')}
            </Typography>
          </Box>
        </FormControl>

        {/* Chinese Description Field with its Translate Button */}
        <Box sx={{ mt: 2, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            onClick={() => handleStreamTranslate(zhDescriptionValue, 'zh-HK', 'en', 'description', setIsTranslatingZhToEnDesc)}
            disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
              isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules || !zhDescriptionValue}
            variant="outlined"
            size="small"
            startIcon={isTranslatingZhToEnDesc ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
          >
            {t('quickListingWizard.translateWithAI')} {t('common.toEnglish')}
          </Button>
        </Box>
        <FormControl fullWidth margin="normal">
          <TextField
            id="zh_description"
            label={`${t('stylistProfileSettings.zh_description')}*`}
            {...register('zh_description', { required: t('common.fieldRequired') })}
            placeholder={t('stylistProfileSettings.enterDescription')}
            multiline
            rows={4}
            fullWidth
            error={!!errors.zh_description}
            helperText={errors.zh_description?.message}
            InputLabelProps={{ shrink: !!zhDescriptionValue }}
            disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
              isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
          />
        </FormControl>

        {/* English Description Field with its Translate Button */}
        <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            onClick={() => handleStreamTranslate(descriptionValue, 'en', 'zh-HK', 'zh_description', setIsTranslatingEnToZhDesc)}
            disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
              isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules || !descriptionValue}
            variant="outlined"
            size="small"
            startIcon={isTranslatingEnToZhDesc ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
          >
            {t('quickListingWizard.translateWithAI')} {t('common.toChinese')}
          </Button>
        </Box>
        <FormControl fullWidth margin="normal">
          <TextField
            id="description"
            label={`${t('stylistProfileSettings.en_description')}`}
            {...register('description')}
            placeholder={t('stylistProfileSettings.enterDescription')}
            multiline
            rows={4}
            fullWidth
            error={!!errors.description}
            helperText={errors.description?.message}
            InputLabelProps={{ shrink: !!descriptionValue }}
            disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
              isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
          />
        </FormControl>
        {/* Assigned Plans Field */}
        <FormControl fullWidth margin="normal">
          <InputLabel>{t('stylistProfileSettings.assignedPlans')}*</InputLabel>
          <Controller
            name="assignedPlans"
            control={control}
            render={({ field }) => (
              <MuiSelect
                {...field}
                labelId="assigned-plans-label"
                label={t('stylistProfileSettings.assignedPlans')}
                multiple
                value={field.value || []}
                onChange={(event) => field.onChange(event.target.value)}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                  isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
              >
                {plans.map(plan => (
                  <MenuItem key={plan._id} value={plan._id}>
                    {plan.name}
                  </MenuItem>
                ))}
              </MuiSelect>
            )}
          />
        </FormControl>


        {/* Certifications Field */}
        <FormControl fullWidth margin="normal">
          <Controller
            name="certifications"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={certifications}
                value={field.value}
                onChange={(_, newValue) => field.onChange(newValue)}
                getOptionLabel={(option) => `${option.name} (${option.zh_name})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('stylistProfileSettings.certifications')}
                    placeholder={t('stylistProfileSettings.selectCertifications')}
                    error={!!errors.certifications}
                    helperText={errors.certifications?.message}
                    disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                      isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                      <Box>
                        <Typography variant="body1">
                          {option.name} ({option.zh_name})
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.org_name} ({option.zh_org_name})
                        </Typography>
                      </Box>
                    </li>
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...chipProps } = getTagProps({ index });
                    return (
                      <Tooltip
                        key={option._id}
                        title={
                          <Box>
                            <Typography variant="body2">
                              {option.org_name} ({option.zh_org_name})
                            </Typography>
                            <Typography variant="body2">
                              {option.scope} ({option.zh_scope})
                            </Typography>
                            <Typography variant="body2">
                              {option.recognization} ({option.zh_recognization})
                            </Typography>
                          </Box>
                        }
                      >
                        <Chip
                          key={key}
                          {...chipProps}
                          label={`${option.name} (${option.zh_name})`}
                        />
                      </Tooltip>
                    );
                  })
                }
                isOptionEqualToValue={(option, value) => option._id === value._id}
              />
            )}
          />
        </FormControl>

        {/* Sub-Service Types Field */}
        <FormControl fullWidth margin="normal">
          <Controller
            name="subServiceTypes"
            control={control}
            rules={{ validate: value => value.length > 0 || t('common.fieldRequired') }}
            render={({ field }) => (
              <Autocomplete
                multiple
                options={subServiceTypeOptions}
                value={field.value}
                onChange={(_, newValue) => field.onChange(newValue)}
                getOptionLabel={(option) => `${option.name} (${option.zh_name})`}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={`${t('common.subServiceTypes')}*`}
                    placeholder={t('common.selectSubServiceTypes')}
                    error={!!errors.subServiceTypes}
                    helperText={errors.subServiceTypes?.message}
                    disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                      isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
                  />
                )}
                isOptionEqualToValue={(option, value) => option._id === value._id}
              />
            )}
          />
        </FormControl>

        {/* Accept Platform Coupons */}
                 <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('stylistProfileSettings.platformCouponsTooltip')}
          </Typography>
          <Controller
            name="isAcceptPlatformCoupons"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={t('stylistProfileSettings.acceptPlatformCoupons')}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                  isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
              />
            )}
          />
        </Box>

        {/* Deposit Options Section */}
        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>{t('stylistProfileSettings.depositOptions')}</Typography>

        {/* Is Deposit Required */}
        <FormControl fullWidth margin="normal" sx={{ mt: 0 }}>
          <Controller
            name="depositOptions.isRequired"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={field.value} />}
                label={t('stylistProfileSettings.depositRequired')}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                  isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
              />
            )}
          />
        </FormControl>

        {depositIsRequired && (
          <>
            {/* PayMe QR Code Upload */}
            <FormControl fullWidth margin="normal">
              <Typography variant="subtitle1" gutterBottom>
                {t('stylistProfileSettings.uploadPaymeQRCode')}*
              </Typography>
              <Button
                variant="contained"
                component="label"
                fullWidth
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                  isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
              >
                {t('stylistProfileSettings.selectPaymeQRCode')}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handlePaymeQRCodeSelect}
                />
              </Button>
              {/* Only show preview if we have a valid paymeQRCodeKey or a new file */}
              {paymeQRCodeKey && !paymeQRCodeFile && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('stylistProfileSettings.paymeQRCodePreview')}
                  </Typography>
                  <img
                    src={`${import.meta.env.VITE_R2_SALON_ENDPOINT}/${paymeQRCodeKey}`}
                    alt={t('stylistProfileSettings.paymeQRCodePreview')}
                    style={{ width: '150px', height: '150px' }}
                  />
                </Box>
              )}
              {paymeQRCodeFile && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('stylistProfileSettings.paymeQRCodePreview')}
                  </Typography>
                  <img
                    src={URL.createObjectURL(paymeQRCodeFile)}
                    alt={t('stylistProfileSettings.paymeQRCodePreview')}
                    style={{ width: '150px', height: '150px' }}
                  />
                </Box>
              )}
            </FormControl>

            {/* Chinese Deposit Rules Field */}
            <FormControl fullWidth margin="normal">
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle1">
                  {t('stylistProfileSettings.zh_depositRules')}*
                </Typography>
                <Button
                  onClick={() => handleStreamTranslate(zhDepositRules || '', 'zh-HK', 'en', 'depositOptions.rules', setIsTranslatingZhToEnDepositRules)}
                  disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                    isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules || !zhDepositRules}
                  variant="outlined"
                  size="small"
                  startIcon={isTranslatingZhToEnDepositRules ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                  sx={{ mt: 0.5 }}
                >
                  {t('quickListingWizard.translateWithAI')} {t('common.toEnglish')}
                </Button>
              </Box>
              <TextField
                multiline
                rows={4}
                fullWidth
                placeholder={t('stylistProfileSettings.enterDepositRules')}
                value={zhDepositRules || ''}
                onChange={(e) => setValue('depositOptions.zh_rules', e.target.value)}
                error={!!errors.depositOptions?.zh_rules}
                helperText={errors.depositOptions?.zh_rules?.message}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                  isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
              />
            </FormControl>

            {/* English Deposit Rules Field */}
            <FormControl fullWidth margin="normal">
              <Box sx={{ mb: 1 }}>
                <Typography variant="subtitle1">
                  {t('stylistProfileSettings.en_depositRules')}
                </Typography>
                <Button
                  onClick={() => handleStreamTranslate(depositRules || '', 'en', 'zh-HK', 'depositOptions.zh_rules', setIsTranslatingEnToZhDepositRules)}
                  disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                    isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules || !depositRules}
                  variant="outlined"
                  size="small"
                  startIcon={isTranslatingEnToZhDepositRules ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                  sx={{ mt: 0.5 }}
                >
                  {t('quickListingWizard.translateWithAI')} {t('common.toChinese')}
                </Button>
              </Box>
              <TextField
                multiline
                rows={4}
                fullWidth
                placeholder={t('stylistProfileSettings.enterDepositRules')}
                value={depositRules || ''}
                onChange={(e) => setValue('depositOptions.rules', e.target.value)}
                error={!!errors.depositOptions?.rules}
                helperText={errors.depositOptions?.rules?.message}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                  isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
              />
            </FormControl>
          </>
        )}

        {/* Showcases Section */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" sx={{ mb: 2 }}>{t('stylistProfileSettings.myShowcases')}</Typography>

        {isLoadingShowcases ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {t('stylistProfileSettings.showcaseCount', '{{count}} showcase(s)', { count: showcases.length })}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => setShowShowcaseForm(true)}
                disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
                  isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules}
              >
                {t('stylistProfileSettings.addShowcase')}
              </Button>
            </Box>

            {showcases.length > 0 ? (
              <Grid container spacing={2}>
                {showcases.map((showcase) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={showcase._id}>
                    <ShowcaseCard
                      showcase={showcase}
                      onEdit={handleEditShowcase}
                      onDelete={handleDeleteShowcase}
                      showActions={true}
                      showStatus={true}
                      showContent={true}
                      contentMaxLength={50}
                      elevation={1}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 4,
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: '1px dashed',
                  borderColor: 'grey.300'
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('stylistProfileSettings.noShowcases')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('stylistProfileSettings.showcaseHint')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Sticky Footer */}
      <Box sx={{
        flexShrink: 0,
        p: 2,
        backgroundColor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
        boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
      }}>
        <Button
          type="submit"
          variant="contained"
          color="primary"
          disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
            isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules || isSubmitting}
          startIcon={isSubmitting && <CircularProgress size={16} color="inherit" />}
          sx={{ mr: 2 }}
        >
          {isSubmitting ? t('common.saving') : t('stylistProfileSettings.saveChanges')}
        </Button>
        {onClose && (
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isTranslatingEnToZhDesc || isTranslatingZhToEnDesc ||
            isTranslatingEnToZhDepositRules || isTranslatingZhToEnDepositRules || isSubmitting}
        >
          {t('stylistProfileSettings.close')}
        </Button>
        )}
      </Box>

      {/* Showcase Form Dialog */}
      {showShowcaseForm && (
        <ShowcaseForm
          selectedStylist={stylist?._id || ''}
          showcase={selectedShowcase as ShowcaseFormProps['showcase']}
          closeModal={handleCloseShowcaseForm}
          isInDialog={true}
        />
      )}

      {/* Delete Showcase Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('stylistProfileSettings.deleteShowcaseTitle')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('stylistProfileSettings.deleteShowcaseConfirmation')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDeleteShowcase}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StylistProfileSettings;
